'use client'

import { useState, useEffect } from 'react'
import { useServices } from '../hooks/useServices'
import { useEventServices } from '../hooks/useEventServices'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import type { Service } from '@planner/types'
import { Plus, X } from 'lucide-react'

interface EventServicesConfiguratorProps {
  eventId: string
  numberOfGuests: number
}

export function EventServicesConfigurator({
  eventId,
  numberOfGuests,
}: EventServicesConfiguratorProps) {
  const { services, loading: servicesLoading } = useServices(false) // Only active services
  const { eventServices, loading: eventServicesLoading, addEventService, updateEventService, removeEventService, calculateTotal } = useEventServices(eventId)
  const [selectedServices, setSelectedServices] = useState<Record<string, { quantity: number; service: Service }>>({})

  useEffect(() => {
    // Initialize selected services from eventServices
    const initial: Record<string, { quantity: number; service: Service }> = {}
    eventServices.forEach((es) => {
      if (es.service) {
        initial[es.service_id] = {
          quantity: es.quantity,
          service: es.service as Service,
        }
      }
    })
    setSelectedServices(initial)
  }, [eventServices])


  const handleRemoveService = async (serviceId: string) => {
    const newSelected = { ...selectedServices }
    delete newSelected[serviceId]
    setSelectedServices(newSelected)
    await removeEventService(serviceId)
  }

  const handleQuantityChange = async (serviceId: string, quantity: number) => {
    const serviceData = selectedServices[serviceId]
    if (!serviceData) return

    const newQuantity = Math.max(0, quantity)
    setSelectedServices({
      ...selectedServices,
      [serviceId]: {
        ...serviceData,
        quantity: newQuantity,
      },
    })

    // Save to database immediately
    if (newQuantity > 0) {
      await updateEventService(serviceId, newQuantity, serviceData.service.price)
    } else {
      await removeEventService(serviceId)
    }
  }

  const getDefaultQuantity = (service: Service): number => {
    switch (service.pricing_type) {
      case 'per_person':
        // Jeśli mamy liczbę gości z wydarzenia, użyj jej, w przeciwnym razie 0 (użytkownik wpisze sam)
        return numberOfGuests > 0 ? numberOfGuests : 0
      case 'per_hour':
        return 1 // Domyślnie 1 godzina
      case 'per_unit':
        return 1 // Domyślnie 1 sztuka
      case 'fixed':
        return 1 // Domyślnie 1 dla stałej ceny
      default:
        return 1
    }
  }

  const getQuantityLabel = (service: Service): string => {
    switch (service.pricing_type) {
      case 'per_person':
        return `Ilość osób (os.)`
      case 'per_hour':
        return `Ilość godzin (godz.)`
      case 'per_unit':
        return `Ilość sztuk (${service.unit_label})`
      case 'fixed':
        return `Ilość (${service.unit_label})`
      default:
        return `Ilość (${service.unit_label})`
    }
  }

  const getQuantityStep = (service: Service): string => {
    switch (service.pricing_type) {
      case 'per_hour':
        return '0.5' // Pozwól na pół godziny
      case 'per_person':
      case 'per_unit':
      case 'fixed':
      default:
        return '1' // Tylko liczby całkowite
    }
  }

  const getQuantityPlaceholder = (service: Service): string => {
    switch (service.pricing_type) {
      case 'per_person':
        // Jeśli mamy liczbę gości, pokaż ją jako przykład, w przeciwnym razie zachęć do wpisania
        return numberOfGuests > 0 ? `np. ${numberOfGuests}` : 'Wpisz liczbę osób'
      case 'per_hour':
        return 'np. 2.5'
      case 'per_unit':
        return 'np. 1'
      case 'fixed':
        return '1'
      default:
        return ''
    }
  }

  const handleAddService = async (service: Service) => {
    const defaultQuantity = getDefaultQuantity(service)
    setSelectedServices({
      ...selectedServices,
      [service.id]: {
        quantity: defaultQuantity,
        service,
      },
    })
    // Save to database immediately
    await addEventService(service.id, defaultQuantity, service.price)
  }

  // Group services by category
  const servicesByCategory = services.reduce((acc: Record<string, Service[]>, service: Service) => {
    const categoryName = service.service_category?.name || 'Bez kategorii'
    if (!acc[categoryName]) {
      acc[categoryName] = []
    }
    acc[categoryName].push(service)
    return acc
  }, {})

  // Get available services (not yet selected)
  const getAvailableServices = (categoryServices: Service[]) => {
    return categoryServices.filter((s) => !selectedServices[s.id])
  }

  const getPricingTypeLabel = (pricingType: string) => {
    switch (pricingType) {
      case 'per_person':
        return 'za osobę'
      case 'per_unit':
        return 'za sztukę'
      case 'per_hour':
        return 'za godzinę'
      case 'fixed':
        return 'stała cena'
      default:
        return ''
    }
  }

  const calculateServiceTotal = (serviceId: string) => {
    const data = selectedServices[serviceId]
    if (!data) return 0
    return data.quantity * data.service.price
  }

  if (servicesLoading || eventServicesLoading) {
    return <div className="p-4">Ładowanie usług...</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Usługi dodatkowe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selected Services */}
          {Object.keys(selectedServices).length > 0 && (
            <div className="space-y-4">
              <h3 className="font-medium">Wybrane usługi</h3>
              {Object.entries(selectedServices).map(([serviceId, data]) => {
                if (data.quantity <= 0) return null
                return (
                  <div key={serviceId} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium">{data.service.name}</h4>
                        {data.service.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {data.service.description}
                          </p>
                        )}
                        <div className="text-sm text-muted-foreground mt-2">
                          {data.service.price.toFixed(2)} zł {getPricingTypeLabel(data.service.pricing_type)}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveService(serviceId)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor={`quantity-${serviceId}`}>
                        {getQuantityLabel(data.service)}
                      </Label>
                      <Input
                        id={`quantity-${serviceId}`}
                        type="number"
                        min="0"
                        step={getQuantityStep(data.service)}
                        value={data.quantity || ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0
                          handleQuantityChange(serviceId, value)
                        }}
                        placeholder={getQuantityPlaceholder(data.service)}
                        className="mt-1"
                      />
                      {data.service.pricing_type === 'per_hour' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Możesz wpisać ułamki, np. 2.5 dla 2 godzin i 30 minut
                        </p>
                      )}
                      <div className="text-sm font-medium mt-2">
                        Razem: {calculateServiceTotal(serviceId).toFixed(2)} zł
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Available Services by Category */}
          <div className="space-y-6">
            {Object.entries(servicesByCategory).map(([categoryName, categoryServices]) => {
              const availableServices = getAvailableServices(categoryServices)
              if (availableServices.length === 0) return null

              return (
                <div key={categoryName}>
                  <h3 className="font-medium mb-3">{categoryName}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableServices.map((service: Service) => (
                      <Card key={service.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium">{service.name}</h4>
                              {service.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {service.description}
                                </p>
                              )}
                              <div className="text-sm text-muted-foreground mt-2">
                                {service.price.toFixed(2)} zł {getPricingTypeLabel(service.pricing_type)}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddService(service)}
                            className="w-full mt-3"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Dodaj
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Total */}
          {Object.keys(selectedServices).length > 0 && (
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Suma usług:</span>
                <span className="text-2xl font-bold">{calculateTotal().toFixed(2)} zł</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

